import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import queryString from 'query-string';
import { useHistory } from 'react-router';
import { useRouter } from 'next/router';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { TextField } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Dialog, DialogTitle, DialogContent } from 'design-system';
import { makeStyles } from 'design-system/styles';
import ProductIcon from 'design-system/ProductIcon';
import useCorpSearch from 'react-scale/hooks/useCorpSearch';
import { parseLookupId } from 'corp/lib/parseInputText';
import {
  APISearchResult,
  SearchMetaEntry,
  SearchResultActionType,
  SearchResultType,
} from '@scale/shared/corpSearch/types';
import apiFetch from 'react-scale/lib/api-fetch';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { RemoAdminLookupLink } from 'react-scale/components/remoAdmin/remoAdminLookupLink';

enum KeyCode {
  Escape = 27,
  ArrowUp = 38,
  ArrowDown = 40,
  Enter = 13,
  Tab = 9,
}

const ACCENT_COLOR = '#DD45D3';

interface SearchDialogProps {
  open: boolean;
  isRemoAdmin?: boolean;
  onClose: () => void;
}

const SEARCH_TYPES: SearchResultType[] = [SearchResultType.CorpLookup, SearchResultType.CorpSearch];
const QUICK_ACTION_TYPES: SearchResultType[] = [
  SearchResultType.Project,
  SearchResultType.Course,
  SearchResultType.Customer,
  SearchResultType.Tasker,
  SearchResultType.Team,
  SearchResultType.CDSUrl,
  SearchResultType.ProjectGroup,
];

const useSearchStyles = makeStyles<{ accentColor: string }>({
  searchDialog: {
    position: 'absolute',
    top: 100,
    background: 'black',
  },
  searchDialogContent: {
    background: 'black',
    display: 'flex',
    flexDirection: 'column',
    width: 640,
    maxHeight: 400,
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  resultRows: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  searchBar: {
    width: '100%',
    height: 70,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
    position: 'sticky',
    left: 0,
    top: 0,
    background: 'black',
  },
  searchInput: ({ accentColor }: { accentColor: string }) => ({
    color: 'white',
    fontSize: '20px',
    fontWeight: 400,
    paddingLeft: 20,
    caretColor: accentColor,
  }),
  resultRow: {
    color: 'white',
    fontSize: 16,
    lineHeight: '24px',
    flexBasis: 60,
    flexShrink: 0,
    fontWeight: 400,
    paddingTop: 8,
    paddingBottom: 8,
    borderLeft: 'solid 2px transparent',
    cursor: 'pointer',
    '&:last-child': {
      marginBottom: 8,
    },
    userSelect: 'none',
    // cursor: 'pointer',
    // Styles for displaying children
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconWrapper: {
    height: 24,
    width: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  resultMainContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingLeft: 20,
    paddingRight: 20,
  },
  resultRowLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  resultRowType: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 13,
    lineHeight: '20px',
  },
  quickActionText: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 13,
    marginRight: 20,
  },
  keySpan: {
    padding: '2px 4px',
    background: '#404040',
    borderRadius: 4,
    marginLeft: 4,
    color: 'white',
  },
  resultRowContents: {},
  focused: ({ accentColor }: { accentColor: string }) => ({
    background: '#1D1D1D',
    borderColor: accentColor,
  }),
});

// hacky way to display results for remoadmin, without modifying the useCorpSearch hook
const RemoAdminResultsDialog: React.FC<{ results: any[]; onClose: () => void }> = props => {
  const classes = useSearchStyles({ accentColor: ACCENT_COLOR });
  const { results, onClose } = props;

  return (
    <Dialog open={results.length > 1} onClose={_ => onClose()}>
      <div style={{ minWidth: '600px' }}>
        <DialogTitle>Search Results</DialogTitle>
        <DialogContent>
          {results.map(result => {
            return (
              <div className={classes.resultMainContent} key={result.id}>
                <div className={classes.iconWrapper}>
                  <ResultIcon type={result.type} description={result.description} />
                </div>
                <div style={{ marginLeft: 16 }}>
                  <RemoAdminLookupLink id={result.id} displayValue={result.name} />
                </div>
              </div>
            );
          })}
        </DialogContent>
      </div>
    </Dialog>
  );
};

const SearchDialog: React.FC<SearchDialogProps> = ({ open, onClose }) => {
  const { isRemoAdmin } = useRemoAdminCtx();
  const classes = useSearchStyles({ accentColor: ACCENT_COLOR });
  const inputRef = React.createRef<HTMLInputElement>();
  const [searchText, setSearchText] = React.useState<string>('');
  const { results } = useCorpSearch(searchText, isRemoAdmin);
  const [focusIndex, setFocusIndex] = React.useState(0);
  const [completedQuickActions, setCompletedQuickActions] = React.useState<number[]>([]);
  const [remoAdminResults, setRemoAdminResults] = React.useState<any[]>([]);

  const history = useHistory();
  const router = useRouter();

  const handleClose = () => {
    onClose();
    setFocusIndex(0);
    setSearchText('');
  };

  React.useEffect(() => {
    setFocusIndex(searchText && results.length > 1 ? 1 : 0);
    setCompletedQuickActions([]);
  }, [results, searchText]);

  React.useEffect(() => {
    if (remoAdminResults.length === 1) {
      const url = new URL(window.location.href);
      const basePathname = window.location.href.includes('/expert')
        ? 'expert/outlieradmin'
        : 'remoadmin';
      url.pathname = `/${basePathname}/tools/lookup/${remoAdminResults[0].id}`;
      window.open(url, '_blank');
      handleClose();
    }
  }, [remoAdminResults]);

  const onKeyDown = (event: React.KeyboardEvent<any>): void => {
    if (results.length === 0) {
      return;
    }
    if (event.keyCode === KeyCode.ArrowDown) {
      const nextIndex = focusIndex + 1;
      if (nextIndex >= results.length) {
        return;
      } else {
        setFocusIndex(nextIndex);
      }
      event.preventDefault();
      return;
    }
    if (event.keyCode === KeyCode.ArrowUp) {
      const nextIndex = focusIndex - 1;
      if (nextIndex >= 0) {
        setFocusIndex(nextIndex);
      }
      event.preventDefault();
      return;
    }
    if (event.keyCode === KeyCode.Enter) {
      const shouldUseFirstSearchResult = event.shiftKey;
      handleResultAction(shouldUseFirstSearchResult ? results[0] : results[focusIndex], {
        shouldOpenInNewTab: event.metaKey || event.altKey,
        suppressQuickLookup: event.shiftKey,
      });
      return;
    }
    if (event.keyCode === KeyCode.Tab) {
      handleQuickAction(results[focusIndex]);
      event.preventDefault();
      return;
    }
  };

  const handleRemoAdminSearch = (queryString: string) => {
    const parsedId = parseLookupId(queryString);
    if (parsedId) {
      const url = new URL(window.location.href);
      const basePathname = window.location.href.includes('/expert')
        ? 'expert/outlieradmin'
        : 'remoadmin';
      url.pathname = `/${basePathname}/tools/lookup/${parsedId}`;
      window.open(url, '_blank');
    } else {
      const encodedQuery = encodeURIComponent(queryString);
      const searchEndpoint = `corp-api/search?q=${encodedQuery}&remofuzzy=1&exact=1`;
      const opts = { allowedStatusCodes: [404] };
      apiFetch<APISearchResult[] | undefined>(null, searchEndpoint, opts)
        .then(result => {
          if (!result || !result.length) {
            alert(`No results for ${encodedQuery}`);
          } else {
            setRemoAdminResults(result);
          }
          return result;
        })
        .catch(_ => {
          /* do nothing */ return;
        });
    }
  };

  const handleResultAction = (
    result: SearchMetaEntry,
    opts?: { shouldOpenInNewTab: boolean; suppressQuickLookup?: boolean },
  ) => {
    const goToPage = ({ pathname, search }: { pathname: string; search?: string }) => {
      if (opts?.shouldOpenInNewTab) {
        window.open(_.compact(['/corp' + pathname, search]).join('?'), '_blank');
      } else {
        history.push({ pathname, search });
        // We need to do this to update the NextJS pages
        router.replace(_.compact(['/corp' + pathname, search]).join('?'));
      }
    };

    if (isRemoAdmin) {
      const searchStr =
        (result.action.type === SearchResultActionType.CorpLookup && result?.action?.id) ||
        searchText.trim();
      if (searchStr) handleRemoAdminSearch(searchStr);
      handleClose();
    } else if (result.action.type === SearchResultActionType.Link) {
      const pathname = '/' + result.action.path;
      const search = result.action.query ? queryString.stringify(result.action.query) : '';
      goToPage({ pathname, search });

      handleClose();
    } else if (result.action.type === SearchResultActionType.CorpSearch) {
      // This path does not support RemoAdmin for now
      const pathname = '/search';
      const search = queryString.stringify({
        q: searchText.trim(),
        ...(opts?.suppressQuickLookup ? { skipQuick: 1 } : {}),
      });

      goToPage({ pathname, search });
      handleClose();
    } else if (result.action.type === SearchResultActionType.CorpLookup) {
      const parsedId = parseLookupId(searchText);
      const pathNamePrefix = '/lookup/';
      const pathname = pathNamePrefix + encodeURIComponent(result.action.id || parsedId || '');

      goToPage({ pathname });
      handleClose();
    }
  };

  const handleQuickAction = (result: SearchMetaEntry) => {
    if (!QUICK_ACTION_TYPES.includes(result.type)) {
      return;
    }
    if (result.type === SearchResultType.Project) {
      copy(result.id || '');
    }
    if (result.action.type === SearchResultActionType.CorpLookup && result.action.id) {
      copy(result.action.id);
    }
    setCompletedQuickActions(oldValues => _.uniq([...oldValues, focusIndex]));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.searchDialog }}
        transitionDuration={0}
      >
        <div className={classes.searchDialogContent}>
          <TextField
            className={classes.searchBar}
            autoFocus
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search..."
            ref={inputRef}
            inputRef={inputRef}
            InputProps={{
              classes: {
                root: classes.searchInput,
              },
              disableUnderline: true,
            }}
          />
          <div className={classes.resultRows}>
            {results.map((result, index) => (
              <SearchResultRow
                key={index}
                result={result}
                focused={index === focusIndex}
                completedQuickAction={completedQuickActions.includes(index)}
                searchText={searchText}
                onClick={e =>
                  handleResultAction(result, {
                    shouldOpenInNewTab: e.metaKey || e.altKey,
                  })
                }
                onMouseEnter={() => setFocusIndex(index)}
              />
            ))}
          </div>
        </div>
      </Dialog>
      <RemoAdminResultsDialog results={remoAdminResults} onClose={() => setRemoAdminResults([])} />
    </>
  );
};

interface SearchResultRowProps {
  result: SearchMetaEntry;
  focused: boolean;
  completedQuickAction: boolean;
  searchText: string;
  onClick: React.MouseEventHandler;
  onMouseEnter: () => void;
}

const SearchResultRow: React.FC<SearchResultRowProps> = ({
  result,
  focused,
  completedQuickAction,
  searchText,
  onClick,
  onMouseEnter,
}) => {
  const ref = React.createRef<HTMLDivElement>();
  const [showingCopiedMessage, setShowingCopiedMessage] = React.useState(false);

  React.useEffect(() => {
    if (completedQuickAction) {
      setShowingCopiedMessage(true);
      setTimeout(() => {
        setShowingCopiedMessage(false);
      }, 3000);
    }
  }, [completedQuickAction]);

  React.useEffect(() => {
    if (focused) {
      // @ts-ignore
      if (ref.current?.scrollIntoViewIfNeeded) {
        // @ts-ignore
        ref.current!.scrollIntoViewIfNeeded();
      }
    }
  }, [ref, focused]);

  const classes = useSearchStyles({ accentColor: ACCENT_COLOR });
  return (
    <div
      className={classnames(classes.resultRow, {
        [classes.focused]: focused,
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      ref={ref}
    >
      <div className={classes.resultMainContent}>
        <div className={classes.iconWrapper}>
          <ResultIcon type={result.type} description={result.description} />
        </div>
        <div style={{ marginLeft: 16 }}>
          <div className={classes.resultRowLabel}>
            {SEARCH_TYPES.includes(result.type) ? `Search "${searchText}"` : result.name}
          </div>
          {!SEARCH_TYPES.includes(result.type) && (
            <div className={classes.resultRowType}>
              {result.type === SearchResultType.CDSUrl
                ? _.upperCase(result.type)
                : _.capitalize(result.type)}
              {result.description && ` | ${result.description}`}
            </div>
          )}
        </div>
      </div>
      {QUICK_ACTION_TYPES.includes(result.type) && (
        <div style={{ opacity: focused ? 1 : 0 }} className={classes.quickActionText}>
          {showingCopiedMessage ? (
            'Copied!'
          ) : (
            <span>
              copy ID
              <span className={classes.keySpan}>Tab</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

interface ResultIconProps {
  type: string;
  description?: string;
}

const FolderOpenIcon = <FontAwesomeIcon icon="folder-open" color="#CCCCCC" />;

const ResultIcon: React.FC<ResultIconProps> = ({ type, description }) => {
  switch (type.toLowerCase()) {
    case SearchResultType.Project:
      return <ProductIcon type={description} /> || FolderOpenIcon;
    case SearchResultType.Tool:
      return <FontAwesomeIcon icon="tools" color="#CCCCCC" />;
    case SearchResultType.Customer:
      return <FontAwesomeIcon icon="building" color="#CCCCCC" />;
    case SearchResultType.Tasker:
      return <FontAwesomeIcon icon="user" color="#CCCCCC" />;
    case SearchResultType.Course:
      return <FontAwesomeIcon icon="clipboard-check" color="#CCCCCC" />;
    case SearchResultType.Team:
      return <FontAwesomeIcon icon="user-friends" color="#CCCCCC" />;
    case SearchResultType.CDSUrl:
      return <FontAwesomeIcon icon="file" color="#CCCCCC" />;
    case 'demo':
      return <FontAwesomeIcon icon="play-circle" color="#CCCCCC" />;
    case SearchResultType.CorpSearch:
      return <FontAwesomeIcon icon="search" color="#CCCCCC" />;
    case SearchResultType.CorpLookup:
      return <FontAwesomeIcon icon="search" color="#CCCCCC" />;
    case SearchResultType.Page:
      return <FontAwesomeIcon icon="browser" color="#CCCCCC" />;
    default:
      return <FontAwesomeIcon icon="tools" color="#CCCCCC" />;
  }
};

export default SearchDialog;
